/**
 * Theme switcher — Auto / Light / Dark.
 *
 * The CSS exposes two concrete skins: the default `:root` (light) and
 * `:root[data-theme="dark"]`. "Auto" is not a CSS state — we resolve it to a
 * concrete light/dark value here (and in the inline boot script in index.html,
 * to avoid a flash) by reading the OS `prefers-color-scheme`. The user's CHOICE
 * (auto | light | dark) is what we persist; the resolved value is what lands on
 * the <html> element.
 *
 * The old dark-glass design lives on the `legacy-ui` git branch.
 */
export type ThemeChoice = 'auto' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const KEY = 'princhess.theme';

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Map a user choice to the concrete skin that should be on <html>. */
export function resolveTheme(choice: ThemeChoice): ResolvedTheme {
  return choice === 'auto' ? (systemPrefersDark() ? 'dark' : 'light') : choice;
}

/** The browser-chrome colour (status bar / PWA theme-color) per skin. */
function chromeColor(resolved: ResolvedTheme): string {
  return resolved === 'dark' ? '#0f131c' : '#f3f4fb';
}

/** Apply + persist a choice. Sets the resolved skin on <html> immediately. */
export function applyTheme(choice: ThemeChoice): void {
  const resolved = resolveTheme(choice);
  document.documentElement.dataset.theme = resolved;
  document.documentElement.dataset.themeChoice = choice;
  try { localStorage.setItem(KEY, choice); } catch { /* private mode */ }
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', chromeColor(resolved));
}

/** Load the persisted choice, migrating old theme names → auto. */
export function loadTheme(): ThemeChoice {
  let v: string | null = null;
  try { v = localStorage.getItem(KEY); } catch { /* private mode */ }
  return v === 'light' || v === 'dark' || v === 'auto' ? v : 'auto';
}

/** Cycle order for a single toggle button: auto → light → dark → auto. */
export function nextTheme(choice: ThemeChoice): ThemeChoice {
  return choice === 'auto' ? 'light' : choice === 'light' ? 'dark' : 'auto';
}

/** Inline toggle icons (svgrepo), recoloured via currentColor so the button can
 *  tint them a muted grey. Light = sun, Dark = split moon, Auto = half circle. */
const ICON_LIGHT = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 0a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0V1a1 1 0 0 1 1-1ZM4.929 3.515a1 1 0 0 0-1.414 1.414l2.828 2.828a1 1 0 0 0 1.414-1.414L4.93 3.515ZM1 11a1 1 0 1 0 0 2h4a1 1 0 1 0 0-2H1ZM18 12a1 1 0 0 1 1-1h4a1 1 0 1 1 0 2h-4a1 1 0 0 1-1-1ZM17.657 16.243a1 1 0 0 0-1.414 1.414l2.828 2.828a1 1 0 1 0 1.414-1.414l-2.828-2.828ZM7.757 17.657a1 1 0 1 0-1.414-1.414L3.515 19.07a1 1 0 1 0 1.414 1.414l2.828-2.828ZM20.485 4.929a1 1 0 0 0-1.414-1.414l-2.828 2.828a1 1 0 1 0 1.414 1.414l2.828-2.828ZM13 19a1 1 0 1 0-2 0v4a1 1 0 1 0 2 0v-4ZM12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10Z"/></svg>';
const ICON_DARK = '<svg viewBox="0 0 48 48" fill="currentColor" aria-hidden="true"><path d="M14,24A10,10,0,0,0,24,34V14A10,10,0,0,0,14,24Z"/><path d="M24,2A22,22,0,1,0,46,24,21.9,21.9,0,0,0,24,2ZM6,24A18.1,18.1,0,0,1,24,6v8a10,10,0,0,1,0,20v8A18.1,18.1,0,0,1,6,24Z"/></svg>';
const ICON_AUTO = '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.9955 11.7115L22.2448 11.6721C22.2326 11.2847 22.0414 10.9249 21.7272 10.698C21.413 10.4711 21.0113 10.4029 20.6397 10.5132L20.9955 11.7115ZM12.2885 3.00454L13.4868 3.36028C13.5971 2.98873 13.5289 2.58703 13.302 2.2728C13.0751 1.95857 12.7153 1.76736 12.3279 1.75516L12.2885 3.00454ZM20.6397 10.5132C20.1216 10.667 19.5716 10.75 19 10.75V13.25C19.815 13.25 20.6046 13.1314 21.3512 12.9098L20.6397 10.5132ZM19 10.75C15.8244 10.75 13.25 8.17564 13.25 5H10.75C10.75 9.55635 14.4437 13.25 19 13.25V10.75ZM13.25 5C13.25 4.42841 13.333 3.87841 13.4868 3.36028L11.0902 2.64879C10.8686 3.39542 10.75 4.18496 10.75 5H13.25ZM12 4.25C12.0834 4.25 12.1665 4.25131 12.2492 4.25392L12.3279 1.75516C12.219 1.75173 12.1097 1.75 12 1.75V4.25ZM4.25 12C4.25 7.71979 7.71979 4.25 12 4.25V1.75C6.33908 1.75 1.75 6.33908 1.75 12H4.25ZM12 19.75C7.71979 19.75 4.25 16.2802 4.25 12H1.75C1.75 17.6609 6.33908 22.25 12 22.25V19.75ZM19.75 12C19.75 16.2802 16.2802 19.75 12 19.75V22.25C17.6609 22.25 22.25 17.6609 22.25 12H19.75ZM19.7461 11.7508C19.7487 11.8335 19.75 11.9166 19.75 12H22.25C22.25 11.8903 22.2483 11.781 22.2448 11.6721L19.7461 11.7508Z"/></svg>';

/** SVG icon + label for the toggle UI, reflecting the current choice. */
export function themeBadge(choice: ThemeChoice): { icon: string; label: string } {
  if (choice === 'light') return { icon: ICON_LIGHT, label: 'Light' };
  if (choice === 'dark') return { icon: ICON_DARK, label: 'Dark' };
  return { icon: ICON_AUTO, label: 'Auto' };
}

/**
 * Keep "Auto" honest: when the OS flips light↔dark and the user is on Auto,
 * re-resolve. No-op when the user has pinned a concrete theme. Call once at boot.
 */
export function watchSystemTheme(): void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => { if (loadTheme() === 'auto') applyTheme('auto'); };
  if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onChange);
  else if (typeof mq.addListener === 'function') mq.addListener(onChange); // older Safari
}
